import { Building2, Compass, ExternalLink } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const BauherrArchitekt = ({ company }) => {
    const t = useT();
    const arch = company?.architect || {};

    return (
        <section
            id="team"
            className="bg-white py-24 sm:py-32"
            data-testid="bauherr-architekt-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-6">
                        <span className="overline">{t({ de: "12 / 12 — Wer baut für Sie", en: "12 / 12 — Who builds for you" })}</span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Bauherr &", en: "Developer &" })}
                            <span className="italic text-brand-500"> {t({ de: "Architekt.", en: "architect." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="body-md">
                            {t({
                                de: "Persönlich, regional, erfahren. Hinter dem Projekt G21 steht ein eingespieltes Team aus erfahrenem Bauträger und einem renommierten Wiesbadener Architekturbüro.",
                                en: "Personal, regional, experienced. Behind project G21 stands a well-rehearsed team of an experienced developer and a renowned Wiesbaden architectural office.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {/* Bauherr */}
                    <article className="bg-cream p-8 lg:p-10 group" data-testid="bauherr-card">
                        <div className="flex items-start justify-between mb-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-brand-500 text-white">
                                <Building2 size={20} strokeWidth={1.5} />
                            </div>
                            <span className="overline">{t({ de: "Bauherr", en: "Developer" })}</span>
                        </div>
                        <h3 className="font-heading text-2xl text-ink mb-1">
                            {company?.name || "G21 Projekt GmbH"}
                        </h3>
                        {company?.directors && (
                            <p className="font-body text-sm text-stone-500 mb-5">
                                {t({ de: "Geschäftsführung:", en: "Managing directors:" })} {company.directors}
                            </p>
                        )}
                        <p className="font-body text-sm text-stone-700 leading-relaxed mb-5">
                            {t({
                                de: "Direktverkauf an Eigennutzer und Kapitalanleger. Persönliche Beratung, kurze Wege, direkter Ansprechpartner durch die gesamte Bauphase.",
                                en: "Direct sales to owner-occupiers and investors. Personal advice, short paths, a direct point of contact throughout the build phase.",
                            })}
                        </p>
                        <dl className="space-y-2 font-body text-sm">
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                <dt className="overline text-stone-400 sm:w-32 shrink-0">Hauptsitz</dt>
                                <dd className="text-stone-700">{company?.address}</dd>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                <dt className="overline text-stone-400 sm:w-32 shrink-0">Bauvorhaben</dt>
                                <dd className="text-stone-700">{company?.project_address}</dd>
                            </div>
                            {company?.registry && (
                                <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                    <dt className="overline text-stone-400 sm:w-32 shrink-0">Register</dt>
                                    <dd className="text-stone-700">{company.registry}</dd>
                                </div>
                            )}
                        </dl>
                    </article>

                    {/* Architekt */}
                    <article className="bg-cream p-8 lg:p-10 group" data-testid="architekt-card">
                        <div className="flex items-start justify-between mb-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-ink text-white">
                                <Compass size={20} strokeWidth={1.5} />
                            </div>
                            <span className="overline">Architekt</span>
                        </div>
                        <h3 className="font-heading text-2xl text-ink mb-1">
                            {arch.name || "Architekturbüro Cornelius"}
                        </h3>
                        {arch.subtitle && (
                            <p className="font-body text-sm text-stone-500 mb-5">{arch.subtitle}</p>
                        )}
                        <p className="font-body text-sm text-stone-700 leading-relaxed mb-5">
                            <strong className="text-ink">{arch.owner}</strong>
                            {arch.note ? <> – {arch.note}</> : null}
                        </p>
                        <dl className="space-y-2 font-body text-sm">
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                <dt className="overline text-stone-400 sm:w-24 shrink-0">Adresse</dt>
                                <dd className="text-stone-700">{arch.address}</dd>
                            </div>
                            {arch.phone && (
                                <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                    <dt className="overline text-stone-400 sm:w-24 shrink-0">Telefon</dt>
                                    <dd className="text-stone-700">
                                        <a href={`tel:${(arch.phone || "").replace(/\s/g, "")}`} className="hover:text-brand-600">
                                            {arch.phone}
                                        </a>
                                    </dd>
                                </div>
                            )}
                            {arch.email && (
                                <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 border-b border-stone-200 py-2">
                                    <dt className="overline text-stone-400 sm:w-24 shrink-0">E-Mail</dt>
                                    <dd className="text-stone-700">
                                        <a href={`mailto:${arch.email}`} className="hover:text-brand-600">
                                            {arch.email}
                                        </a>
                                    </dd>
                                </div>
                            )}
                        </dl>
                        {arch.url && (
                            <a
                                href={arch.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700"
                                data-testid="architect-link"
                            >
                                Profil bei der AK Hessen
                                <ExternalLink size={13} />
                            </a>
                        )}
                    </article>
                </div>
            </div>
        </section>
    );
};
